// Build an A4-portrait PDF of the Chairman briefing.
//
// Why client-side instead of window.print(): Tauri 2's WebView (WKWebView
// on macOS) is unreliable about firing print dialogs and about preserving
// our print stylesheet in the resulting PDF. Doing it ourselves —
// snapshot the live DOM, slice into A4-sized chunks, assemble via jsPDF
// — gives us a deterministic output the chairman can rely on.
//
// The single PNG capture keeps fidelity high (charts come through as
// rasterised pixels of the real on-screen render, not approximated via
// print CSS rewrites). The trade-off: text in the PDF is not selectable.
// That's acceptable for a briefing document; the editable Word download
// is the surface where selection / further editing happens.

import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";

// A4 portrait in mm
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PAGE_MARGIN_MM = 0;

export interface BuildPdfOpts {
  /** DOM node to capture — typically the `.report-page` element. */
  node: HTMLElement;
  /** Resolved at render time (pixels per CSS px). 2 = retina. */
  pixelRatio?: number;
}

export async function buildChairmanPdf(opts: BuildPdfOpts): Promise<Blob> {
  const { node, pixelRatio = 2 } = opts;

  // Use the element's exact bounding rect for the capture. Without
  // explicit width/height, html-to-image can include surrounding
  // whitespace from the centered article margin and the resulting
  // PNG ends up wider than the content — when scaled to fit A4 the
  // content drifts and the right column clips.
  const rect = node.getBoundingClientRect();
  const dataUrl = await toPng(node, {
    backgroundColor: "#ffffff",
    pixelRatio,
    cacheBust: true,
    skipFonts: true,
    width: Math.round(rect.width),
    height: Math.round(rect.height),
    style: {
      // Reset the centering margin so the cloned node renders flush
      // at (0,0) of its capture canvas — no implicit left offset.
      margin: "0",
      boxShadow: "none",
    },
  });
  if (!dataUrl || !dataUrl.startsWith("data:image/png")) {
    throw new Error("html-to-image returned a non-PNG result");
  }

  // Load the dataURL into an Image to read intrinsic dimensions.
  const img = await loadImage(dataUrl);
  const imgWidthPx = img.naturalWidth;
  const imgHeightPx = img.naturalHeight;

  // We want the captured page to span the full A4 width minus margins.
  const usableWidthMm = A4_WIDTH_MM - PAGE_MARGIN_MM * 2;
  const usableHeightMm = A4_HEIGHT_MM - PAGE_MARGIN_MM * 2;

  // Compute mm-per-px so we can convert image height into PDF units.
  const mmPerPx = usableWidthMm / imgWidthPx;
  const totalHeightMm = imgHeightPx * mmPerPx;

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  // If the captured content fits on one page, drop it in directly.
  if (totalHeightMm <= usableHeightMm + 0.5) {
    pdf.addImage(
      dataUrl,
      "PNG",
      PAGE_MARGIN_MM,
      PAGE_MARGIN_MM,
      usableWidthMm,
      totalHeightMm,
      undefined,
      "FAST",
    );
    return pdf.output("blob");
  }

  // Multi-page: slice the image into A4-height chunks via a canvas.
  // Each chunk is rendered onto its own PDF page at the top margin.
  const chunkHeightPx = Math.floor(usableHeightMm / mmPerPx);
  const canvas = document.createElement("canvas");
  canvas.width = imgWidthPx;
  canvas.height = chunkHeightPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("could not get 2d canvas context");

  let yPx = 0;
  let page = 0;
  while (yPx < imgHeightPx) {
    const remainingPx = imgHeightPx - yPx;
    const thisChunkPx = Math.min(chunkHeightPx, remainingPx);
    canvas.height = thisChunkPx;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      img,
      0,
      yPx,
      imgWidthPx,
      thisChunkPx,
      0,
      0,
      imgWidthPx,
      thisChunkPx,
    );
    const chunkDataUrl = canvas.toDataURL("image/png");
    const chunkHeightMm = thisChunkPx * mmPerPx;
    if (page > 0) pdf.addPage("a4", "portrait");
    pdf.addImage(
      chunkDataUrl,
      "PNG",
      PAGE_MARGIN_MM,
      PAGE_MARGIN_MM,
      usableWidthMm,
      chunkHeightMm,
      undefined,
      "FAST",
    );
    yPx += thisChunkPx;
    page += 1;
  }

  return pdf.output("blob");
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = dataUrl;
  });
}
